import {
    type CSSProperties,
    type ReactNode,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    formatCurrency,
    formatDateTime,
    ToggleGroup,
    BarChart,
    Chart
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import type {
    ChartDataRow,
    ChartConfig
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Callout,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "BarChart";

const USAGE_CODE = `<Chart
    config={{
        orders: {
            title: "Monthly orders",
            colorIndex: 1
        }
    }}
    description="Last 12 months, completed orders"
    title="Monthly orders"
>
    <BarChart
        categoryTitle="Month"
        categoryKey="month"
        data={data}
    />
</Chart>`;

const HORIZONTAL_CODE = `<BarChart
    orientation="horizontal"
    categoryTitle="Source"
    categoryKey="source"
    data={data}
/>`;

const STACKED_CODE = `<BarChart
    stackBehaviour="stacked" // or "expanded" for shares of 100
    seriesKeys={["email", "chat", "phone"]}
    categoryKey="month"
    data={data}
/>`;

const DIVERGING_CODE = `<BarChart
    valueFormatter={(value) => formatCurrency({
        value: value * 1000,
        currency: "TRY",
        locale: "tr-TR"
    })}
    colorBehaviour="diverging"
    categoryKey="month"
    data={data}
/>`;

const INTERACTIVE_CODE = `const [activeIndex, setActiveIndex] = useState<number | null>(null);

<BarChart
    onActiveChange={(index) => setActiveIndex(index ?? null)}
    seriesKeys={["desktop", "mobile"]}
    activeIndex={activeIndex}
    highlightKey="mobile"
    categoryKey="day"
    data={data}
/>`;

const STACK_MODES = [
    {
        titleKey: "barChartPage.modeStacked",
        value: "stacked"
    },
    {
        titleKey: "barChartPage.modeExpanded",
        value: "expanded"
    }
] as const;

const HIGHLIGHTS = [
    {
        titleKey: "barChartPage.seriesDesktop",
        value: "desktop"
    },
    {
        titleKey: "barChartPage.seriesMobile",
        value: "mobile"
    }
] as const;

const ORDERS = [
    2140,
    1980,
    1410,
    1320,
    1560,
    1490,
    1620,
    1540,
    1380,
    1450,
    1710,
    1860
];

const ORDER_MONTHS = [
    10,
    11,
    0,
    1,
    2,
    3,
    4,
    5,
    6,
    7,
    8,
    9
];

const SOURCE_VALUES = [
    48210,
    27940,
    18630,
    9870,
    8420,
    4150
];

const SOURCE_KEYS = [
    "organic",
    "direct",
    "social",
    "email",
    "paid",
    "referral"
];

const NEW_CUSTOMERS = [
    412,
    386,
    455,
    431,
    508,
    562
];

const RETURNING_CUSTOMERS = [
    640,
    688,
    702,
    735,
    796,
    842
];

const EMAIL_TICKETS = [
    820,
    760,
    840,
    790,
    870,
    910,
    980,
    1040,
    930,
    880
];

const CHAT_TICKETS = [
    540,
    580,
    620,
    660,
    710,
    760,
    830,
    890,
    860,
    920
];

const PHONE_TICKETS = [
    410,
    390,
    380,
    360,
    340,
    330,
    320,
    300,
    290,
    280
];

const PROFITS = [
    184,
    -62,
    96,
    142,
    -38,
    210,
    168,
    -94,
    126,
    238
];

const DESKTOP_VISITS = [
    893,
    844,
    561,
    599,
    897,
    917,
    953,
    911,
    836,
    613,
    599,
    1011,
    926,
    967,
    976,
    907,
    601,
    616,
    985,
    978,
    959,
    961,
    883,
    611,
    644,
    1036,
    997,
    742,
    498,
    907
];

const MOBILE_VISITS = [
    738,
    651,
    937,
    1003,
    723,
    775,
    757,
    700,
    684,
    1036,
    1032,
    814,
    742,
    768,
    762,
    702,
    1026,
    1056,
    832,
    787,
    723,
    731,
    760,
    1043,
    1072,
    824,
    753,
    812,
    1046,
    721
];

const CURRENCIES = {
    tr: "TRY",
    en: "USD"
} as const;

type StackModeType = (typeof STACK_MODES)[number]["value"];

type HighlightType = (typeof HIGHLIGHTS)[number]["value"];

const SOURCE_SERIES_KEYS = [
    "sessions"
];

const NEW_SERIES_KEYS = [
    "fresh",
    "returning"
];

const TICKET_SERIES_KEYS = [
    "email",
    "chat",
    "phone"
];

const PROFIT_SERIES_KEYS = [
    "profit"
];

const VISIT_SERIES_KEYS = [
    "desktop",
    "mobile"
];

const ORDER_SERIES_KEYS = [
    "orders"
];

const getMonthLabel = (month: number, locale: string, isLong = false) => formatDateTime({
    options: {
        month: isLong ? "long" : "short"
    },
    date: new Date(2026, month, 1),
    locale
});

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("barChartPage.horizontalTitle"),
        id: "horizontal"
    },
    {
        title: t("barChartPage.groupedTitle"),
        id: "grouped"
    },
    {
        title: t("barChartPage.stackedTitle"),
        id: "stacked"
    },
    {
        title: t("barChartPage.divergingTitle"),
        id: "diverging"
    },
    {
        title: t("barChartPage.interactiveTitle"),
        id: "interactive"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const BarChartPage = () => {
    const {
        language,
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const {
        shapeConfigs
    } = NCoreUIKitTheme.useContext();

    const [
        stackBehaviour,
        setStackBehaviour
    ] = useState<StackModeType>("stacked");

    const [
        highlightKey,
        setHighlightKey
    ] = useState<HighlightType>("mobile");

    const [
        activeIndex,
        setActiveIndex
    ] = useState<number | null>(null);

    const chartStyle: CSSProperties = {
        maxWidth: shapeConfigs.contentMaxWidth * 0.75,
        width: "100%"
    };

    const rowStyle: CSSProperties = {
        justifyContent: "center",
        display: "flex",
        width: "100%"
    };

    const shortMonth = (month: number) => getMonthLabel(month, activeLocale);

    const orderRows: Array<ChartDataRow> = ORDER_MONTHS.map((month, index) => ({
        orders: ORDERS[index] ?? 0,
        month: shortMonth(month)
    }));

    const sourceRows: Array<ChartDataRow> = SOURCE_KEYS.map((key, index) => ({
        source: t(`barChartPage.source.${key}`),
        sessions: SOURCE_VALUES[index] ?? 0
    }));

    const customerRows: Array<ChartDataRow> = NEW_CUSTOMERS.map((value, index) => ({
        returning: RETURNING_CUSTOMERS[index] ?? 0,
        month: shortMonth(index + 4),
        fresh: value
    }));

    const ticketRows: Array<ChartDataRow> = EMAIL_TICKETS.map((value, index) => ({
        phone: PHONE_TICKETS[index] ?? 0,
        chat: CHAT_TICKETS[index] ?? 0,
        month: shortMonth(index),
        email: value
    }));

    const profitRows: Array<ChartDataRow> = PROFITS.map((value, index) => ({
        month: shortMonth(index),
        profit: value
    }));

    const visitRows: Array<ChartDataRow> = DESKTOP_VISITS.map((value, index) => ({
        day: formatDateTime({
            date: new Date(2026, 9, index + 1),
            locale: activeLocale,
            options: {
                month: "short",
                day: "numeric"
            }
        }),
        mobile: MOBILE_VISITS[index] ?? 0,
        desktop: value
    }));

    const orderConfig: ChartConfig = {
        orders: {
            title: t("barChartPage.seriesOrders"),
            colorIndex: 1
        }
    };

    const sourceConfig: ChartConfig = {
        sessions: {
            title: t("barChartPage.seriesSessions"),
            colorIndex: 1
        }
    };

    const customerConfig: ChartConfig = {
        returning: {
            title: t("barChartPage.seriesReturning"),
            colorIndex: 2
        },
        fresh: {
            title: t("barChartPage.seriesNew"),
            colorIndex: 1
        }
    };

    const ticketConfig: ChartConfig = {
        email: {
            title: t("barChartPage.seriesEmail"),
            colorIndex: 1
        },
        phone: {
            title: t("barChartPage.seriesPhone"),
            colorIndex: 3
        },
        chat: {
            title: t("barChartPage.seriesChat"),
            colorIndex: 2
        }
    };

    const profitConfig: ChartConfig = {
        profit: {
            title: t("barChartPage.seriesProfit"),
            colorIndex: 1
        }
    };

    const visitConfig: ChartConfig = {
        desktop: {
            title: t("barChartPage.seriesDesktop"),
            colorIndex: 1
        },
        mobile: {
            title: t("barChartPage.seriesMobile"),
            colorIndex: 2
        }
    };

    const formatProfit = (value: number) => `${value < 0 ? "−" : ""}${formatCurrency({
        currency: CURRENCIES[language],
        value: Math.abs(value) * 1000,
        options: {
            maximumFractionDigits: 0
        },
        locale: activeLocale
    })}`;

    const activeDay = activeIndex === null ? undefined : visitRows[activeIndex];

    const handleStackChange = (value: string | Array<string>) => {
        const nextStack = STACK_MODES.find((item) => item.value === value);

        if(nextStack) {
            setStackBehaviour(nextStack.value);
        }
    };

    const handleHighlightChange = (value: string | Array<string>) => {
        const nextHighlight = HIGHLIGHTS.find((item) => item.value === value);

        if(nextHighlight) {
            setHighlightKey(nextHighlight.value);
        }
    };

    const handleActiveChange = (index: number | undefined) => {
        setActiveIndex(index ?? null);
    };

    const renderToggleGroup = (title: string, items: ReadonlyArray<{
        titleKey: string;
        value: string;
    }>, value: string, onChange: (value: string | Array<string>) => void) => <ToggleGroup
        attachBehaviour="attached"
        onValueChange={onChange}
        isClearEnabled={false}
        variant="outline"
        title={title}
        value={value}
        size="small"
    >
        {
            items.map((item) => <ToggleGroup.Item
                title={t(item.titleKey)}
                value={item.value}
                key={item.value}
            />)
        }
    </ToggleGroup>;

    const renderChart = (chart: ReactNode, config: ChartConfig, title: string, description: string, note?: string) => <div
        style={rowStyle}
    >
        <div
            style={chartStyle}
        >
            <Chart
                spreadBehaviour="stretch"
                description={description}
                config={config}
                title={title}
                note={note}
            >
                {chart}
            </Chart>
        </div>
    </div>;

    return <Article>
        <ComponentHeader
            lead={t("barChartPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("barChartPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("barChartPage.overviewCaption")}
            >
                {
                    renderChart(<BarChart
                        categoryTitle={t("barChartPage.month")}
                        seriesKeys={ORDER_SERIES_KEYS}
                        categoryKey="month"
                        data={orderRows}
                    />, orderConfig, t("barChartPage.ordersTitle"), t("barChartPage.ordersDescription"))
                }
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
            <Callout>
                {t("barChartPage.overviewNote")}
            </Callout>
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("barChartPage.horizontalTitle")}
            id="horizontal"
        >
            <Paragraph>
                {t("barChartPage.horizontalText")}
            </Paragraph>
            <Preview
                caption={t("barChartPage.horizontalCaption")}
            >
                {
                    renderChart(<BarChart
                        categoryTitle={t("barChartPage.source")}
                        seriesKeys={SOURCE_SERIES_KEYS}
                        orientation="horizontal"
                        categoryKey="source"
                        data={sourceRows}
                    />, sourceConfig, t("barChartPage.sourcesTitle"), t("barChartPage.sourcesDescription"))
                }
            </Preview>
            <CodeBlock
                code={HORIZONTAL_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("barChartPage.groupedTitle")}
            id="grouped"
        >
            <Paragraph>
                {t("barChartPage.groupedText")}
            </Paragraph>
            <Preview
                caption={t("barChartPage.groupedCaption")}
            >
                {
                    renderChart(<BarChart
                        categoryTitle={t("barChartPage.month")}
                        seriesKeys={NEW_SERIES_KEYS}
                        categoryKey="month"
                        data={customerRows}
                    />, customerConfig, t("barChartPage.customersTitle"), t("barChartPage.customersDescription"))
                }
            </Preview>
        </Section>
        <Section
            title={t("barChartPage.stackedTitle")}
            id="stacked"
        >
            <Paragraph>
                {t("barChartPage.stackedText")}
            </Paragraph>
            <Preview
                caption={t("barChartPage.stackedCaption")}
            >
                {renderToggleGroup("stackBehaviour", STACK_MODES, stackBehaviour, handleStackChange)}
                {
                    renderChart(<BarChart
                        categoryTitle={t("barChartPage.month")}
                        stackBehaviour={stackBehaviour}
                        seriesKeys={TICKET_SERIES_KEYS}
                        categoryKey="month"
                        data={ticketRows}
                    />, ticketConfig, t("barChartPage.ticketsTitle"), t("barChartPage.ticketsDescription"))
                }
            </Preview>
            <CodeBlock
                code={STACKED_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("barChartPage.divergingTitle")}
            id="diverging"
        >
            <Paragraph>
                {t("barChartPage.divergingText")}
            </Paragraph>
            <Preview
                caption={t("barChartPage.divergingCaption")}
            >
                {
                    renderChart(<BarChart
                        categoryTitle={t("barChartPage.month")}
                        seriesKeys={PROFIT_SERIES_KEYS}
                        valueFormatter={formatProfit}
                        colorBehaviour="diverging"
                        categoryKey="month"
                        data={profitRows}
                    />, profitConfig, t("barChartPage.profitTitle"), t("barChartPage.profitDescription"))
                }
            </Preview>
            <CodeBlock
                code={DIVERGING_CODE}
                language="tsx"
            />
            <Callout>
                {t("barChartPage.divergingNote")}
            </Callout>
        </Section>
        <Section
            title={t("barChartPage.interactiveTitle")}
            id="interactive"
        >
            <Paragraph>
                {t("barChartPage.interactiveText")}
            </Paragraph>
            <Preview
                caption={t("barChartPage.interactiveCaption")}
            >
                {renderToggleGroup("highlightKey", HIGHLIGHTS, highlightKey, handleHighlightChange)}
                {
                    renderChart(<BarChart
                        categoryTitle={t("barChartPage.day")}
                        onActiveChange={handleActiveChange}
                        seriesKeys={VISIT_SERIES_KEYS}
                        highlightKey={highlightKey}
                        activeIndex={activeIndex}
                        categoryKey="day"
                        data={visitRows}
                    />, visitConfig, t("barChartPage.visitsTitle"), t("barChartPage.visitsDescription"))
                }
                <Paragraph
                    isMuted={true}
                >
                    {
                        activeDay ?
                            t("barChartPage.activeReadout", [
                                String(activeDay.day),
                                String(activeDay.desktop),
                                String(activeDay.mobile)
                            ])
                        :
                            t("barChartPage.activeEmpty")
                    }
                </Paragraph>
            </Preview>
            <CodeBlock
                code={INTERACTIVE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("barChartPage.a11yPlot"),
                    t("barChartPage.a11yKeyboard"),
                    t("barChartPage.a11yLive"),
                    t("barChartPage.a11yHidden"),
                    t("barChartPage.a11yTable"),
                    t("barChartPage.a11yColor"),
                    t("barChartPage.a11yMotion")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        type: "Array<Record<string, number | string>>",
                        description: t("barChartPage.propData"),
                        name: "data"
                    },
                    {
                        description: t("barChartPage.propCategoryKey"),
                        name: "categoryKey",
                        type: "string"
                    },
                    {
                        description: t("barChartPage.propSeriesKeys"),
                        type: "Array<string>",
                        name: "seriesKeys"
                    },
                    {
                        description: t("barChartPage.propOrientation"),
                        type: "\"vertical\" | \"horizontal\"",
                        defaultValue: "\"vertical\"",
                        name: "orientation"
                    },
                    {
                        description: t("barChartPage.propStackBehaviour"),
                        type: "\"grouped\" | \"stacked\" | \"expanded\"",
                        defaultValue: "\"grouped\"",
                        name: "stackBehaviour"
                    },
                    {
                        description: t("barChartPage.propColorBehaviour"),
                        type: "\"series\" | \"diverging\"",
                        defaultValue: "\"series\"",
                        name: "colorBehaviour"
                    },
                    {
                        description: t("barChartPage.propHighlightKey"),
                        name: "highlightKey",
                        type: "string"
                    },
                    {
                        description: t("barChartPage.propActiveIndex"),
                        type: "number | null",
                        name: "activeIndex"
                    },
                    {
                        description: t("barChartPage.propOnActiveChange"),
                        type: "(index: number | undefined) => void",
                        name: "onActiveChange"
                    },
                    {
                        type: "\"none\" | \"end\" | \"extremes\" | \"all\"",
                        description: t("barChartPage.propLabelBehaviour"),
                        name: "labelBehaviour"
                    },
                    {
                        description: t("barChartPage.propValueFormatter"),
                        type: "(value: number) => string",
                        name: "valueFormatter"
                    },
                    {
                        type: "(value: number | string, index: number) => string",
                        description: t("barChartPage.propCategoryFormatter"),
                        name: "categoryFormatter"
                    },
                    {
                        description: t("barChartPage.propCategoryTitle"),
                        name: "categoryTitle",
                        type: "string"
                    },
                    {
                        description: t("barChartPage.propPlotSize"),
                        type: "\"small\" | \"medium\" | \"large\"",
                        defaultValue: "\"medium\"",
                        name: "plotSize"
                    },
                    {
                        description: t("barChartPage.propTooltipBehaviour"),
                        type: "\"auto\" | \"follow\" | \"pinned\"",
                        name: "tooltipBehaviour"
                    },
                    {
                        type: "(content: ChartTooltipContent) => ReactNode",
                        description: t("barChartPage.propRenderTooltip"),
                        name: "renderTooltip"
                    },
                    {
                        description: t("barChartPage.propSummary"),
                        name: "summary",
                        type: "string"
                    },
                    {
                        description: t("barChartPage.propIsAnimatedOnMount"),
                        name: "isAnimatedOnMount",
                        type: "boolean"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("barChartPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("barChartPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("barChartPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default BarChartPage;
